import React, { useState } from "react";
import {
  Grip,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Send,
  FileSignature,
  ReceiptText,
  Building2,
  Hash,
  Loader,
  Search,
  Check,
  Hourglass,
  Sun,
  Moon,
  LogOut,
  CalendarDays,
  Users,
} from "lucide-react";
import { contract as fallbackContract } from "../../mock";
import { useNavigate } from "react-router-dom";
import { COMPANIES, CompanyLogo } from "../../companies";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";

const PillButton = ({ icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-2 rounded-full border border-transparent bg-[#F0EBE5] px-3 py-2.5 text-sm font-medium text-[#1A1815] transition-colors duration-200 hover:bg-[#E7E0D8] dark:border-white/10 dark:bg-white/5 dark:text-white/85 dark:hover:bg-white/10 sm:px-5"
  >
    <Icon className="h-4 w-4" strokeWidth={1.6} />
    <span className="hidden sm:inline">{label}</span>
  </button>
);

const InfoGroup = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F0EBE5] dark:border dark:border-white/10 dark:bg-white/5">
      <Icon className="h-4 w-4 text-[#1A1815] dark:text-white/70" strokeWidth={1.6} />
    </div>
    <div className="flex flex-col leading-tight">
      <span className="text-[11px] font-medium text-[#8B8680] dark:text-white/45">{label}</span>
      <span className="text-base font-semibold text-[#1A1815] dark:text-white">{value}</span>
    </div>
  </div>
);

const RANGES = [
  { key: "all", label: "All Time" },
  { key: "today", label: "Today" },
  { key: "week", label: "This Week" },
  { key: "month", label: "This Month" },
];

const inRange = (iso, range) => {
  if (range === "all") return true;
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d)) return false;
  const now = new Date();
  if (range === "today") return d.toDateString() === now.toDateString();
  if (range === "week") {
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay());
    start.setHours(0, 0, 0, 0);
    return d >= start;
  }
  if (range === "month")
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  return true;
};

const TopSection = ({
  onAction,
  dark,
  toggleTheme,
  envelope,
  documents = [],
  company,
  onSwitchCompany,
  prevCompany,
  onGoToPrevious,
  companies = COMPANIES,
  user,
  onLogout,
}) => {
  const navigate = useNavigate();
  const [range, setRange] = useState("all");
  const contract = envelope || fallbackContract;
  const countBy = (s, dateField) =>
    documents.filter((d) => d.status === s && inRange(d[dateField] || d.created_at, range))
      .length;
  const progress = {
    signed: { label: "Signed", amount: String(countBy("completed", "signed_at")) },
    pending: { label: "Pending", amount: String(countBy("sent", "created_at")) },
    balance: { label: "Action Required", amount: String(countBy("action_required", "created_at")) },
  };
  return (
    <div className="px-6 pt-6 md:px-10 md:pt-8">
      {/* Header */}
      <header className="flex flex-nowrap items-center justify-between gap-2 sm:gap-4">
        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
          <span className="text-2xl font-extrabold tracking-tight text-[#1A1815] dark:text-white">
            Qs<span className="text-[#2FBF71] dark:text-[#7AFFA1]">.</span>
          </span>
          <div className="flex min-w-0 items-center gap-2 rounded-full bg-[#F0EBE5] py-1.5 pl-1.5 pr-3 dark:border dark:border-white/10 dark:bg-white/5 sm:pr-5">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="company-switcher-trigger"
                  title="Switch company"
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#1A1815] outline-none transition-colors hover:bg-[#F0EBE5] dark:bg-white/5 dark:text-white/60 dark:hover:bg-white/10"
                >
                  <Grip className="h-4 w-4" strokeWidth={1.6} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                sideOffset={12}
                style={{ fontFamily: "Urbanist, sans-serif" }}
                className="w-[360px] overflow-hidden rounded-[26px] border-[#EAE3DB] bg-white p-3 shadow-2xl duration-500 dark:border-white/10 dark:bg-[#141019]"
              >
                {/* Active workspace banner */}
                <div className="mb-3 flex items-center gap-3 rounded-2xl bg-[#F0EBE5] p-3.5 dark:bg-white/[0.06]">
                  <CompanyLogo company={company} className="h-12 w-12 rounded-xl" />
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#8B8680] dark:text-white/45">
                      Active workspace
                    </p>
                    <p className="truncate text-base font-bold text-[#1A1815] dark:text-white">
                      {company.name}
                    </p>
                  </div>
                  <span className="ml-auto flex h-2.5 w-2.5 rounded-full bg-[#2FBF71] shadow-[0_0_10px_2px_rgba(47,191,113,0.5)] dark:bg-[#7AFFA1] dark:shadow-[0_0_10px_2px_rgba(122,255,161,0.6)]" />
                </div>

                <p className="mb-2 px-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-[#B0A99F] dark:text-white/35">
                  Group companies
                </p>
                <div className="grid grid-cols-3 gap-1.5">
                  {companies.map((c, i) => {
                    const active = c.key === company.key;
                    return (
                      <DropdownMenuItem
                        key={c.key}
                        data-testid={`company-option-${c.key}`}
                        onClick={() => onSwitchCompany(c)}
                        style={{ animationDelay: `${i * 35}ms` }}
                        className={`company-tile group flex cursor-pointer flex-col items-center gap-2 rounded-2xl border-2 p-2.5 text-center outline-none transition-all duration-200 focus:bg-transparent ${
                          active
                            ? "border-[#1A1815] bg-[#F6EFE8] dark:border-[#7AFFA1] dark:bg-white/[0.06]"
                            : "border-transparent bg-[#F6EFE8] hover:-translate-y-0.5 hover:border-[#E7E0D8] dark:bg-white/[0.03] dark:hover:border-white/15"
                        }`}
                      >
                        <CompanyLogo company={c} className="h-11 w-11 rounded-xl" />
                        <span className="line-clamp-2 text-[11px] font-semibold leading-tight text-[#1A1815] dark:text-white/85">
                          {c.name}
                        </span>
                      </DropdownMenuItem>
                    );
                  })}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Arrow: quick-jump back to the previously used company */}
            <button
              data-testid="company-prev-btn"
              onClick={onGoToPrevious}
              disabled={!prevCompany}
              title={
                prevCompany
                  ? `Back to ${prevCompany.name}`
                  : "No previous company yet"
              }
              className="hidden h-9 w-9 items-center justify-center rounded-full bg-white text-[#1A1815] outline-none transition-colors hover:bg-[#F0EBE5] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white dark:bg-white/5 dark:text-white/60 dark:hover:bg-white/10 dark:disabled:hover:bg-white/5 sm:flex"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.7} />
            </button>

            <CompanyLogo company={company} className="h-7 w-7 shrink-0 sm:h-8 sm:w-8" />
            <span className="truncate text-base font-semibold text-[#1A1815] dark:text-white sm:text-lg">
              {company.name}
            </span>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1.5 sm:gap-3">
          <PillButton
            icon={dark ? Sun : Moon}
            label={dark ? "Light" : "Dark"}
            onClick={toggleTheme}
          />
          <button
            data-testid="header-search-btn"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F0EBE5] transition-colors hover:bg-[#E7E0D8] dark:border dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10 sm:h-11 sm:w-11">
            <Search className="h-4 w-4 text-[#1A1815] dark:text-white/70" strokeWidth={1.6} />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                title={user ? user.name : "Account"}
                className="flex items-center gap-2 rounded-full outline-none"
              >
                <img
                  src={user?.avatar || "https://i.pravatar.cc/80?img=32"}
                  alt="user"
                  className="h-9 w-9 rounded-full object-cover ring-2 ring-transparent transition-all hover:ring-[color:var(--accent)] sm:h-11 sm:w-11"
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={10}
              style={{ fontFamily: "Urbanist, sans-serif" }}
              className="w-60 rounded-2xl border-[#EAE3DB] bg-white p-2 shadow-xl duration-300 dark:border-white/10 dark:bg-[#141019]"
            >
              <div className="flex items-center gap-3 rounded-xl px-2.5 py-2.5">
                <img
                  src={user?.avatar || "https://i.pravatar.cc/80?img=32"}
                  alt="user"
                  className="h-10 w-10 rounded-full object-cover"
                />
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-[#1A1815] dark:text-white">
                    {user ? user.name : "Guest"}
                  </p>
                  <p className="truncate text-xs text-[#8B8680] dark:text-white/45">
                    {user ? user.role : ""}
                  </p>
                </div>
              </div>
              <div className="my-1 h-px bg-[#EAE3DB] dark:bg-white/10" />
              {user?.is_admin && (
                <DropdownMenuItem
                  data-testid="manage-users-menu-item"
                  onClick={() => navigate("/admin/users")}
                  className="flex cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-sm font-semibold text-[#1A1815] focus:bg-[#F0EBE5] dark:text-white dark:focus:bg-white/10"
                >
                  <Users className="h-4 w-4" strokeWidth={1.9} />
                  Manage users
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                data-testid="logout-btn"
                onClick={onLogout}
                className="flex cursor-pointer items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-sm font-semibold text-[#B42318] focus:bg-[#F2C0C0]/30 dark:text-[#FF8A80] dark:focus:bg-white/5"
              >
                <LogOut className="h-4 w-4" strokeWidth={1.9} />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Hero */}
      <section className="mt-10 flex flex-wrap items-center justify-between gap-8 sm:mt-14 sm:gap-10">
        <div className="flex items-center gap-3 sm:gap-5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#F0EBE5] dark:border dark:border-white/10 dark:bg-white/5 sm:h-16 sm:w-16">
            <ReceiptText className="h-5 w-5 text-[#1A1815] dark:text-white/70 sm:h-6 sm:w-6" strokeWidth={1.4} />
          </div>
          <h1 className="flex flex-wrap items-baseline tracking-tight">
            <span className="text-4xl font-bold leading-none text-[#1A1815] dark:text-white sm:text-6xl md:text-8xl">
              Welcome
            </span>
            <span className="ml-2 text-xl font-medium text-[#C9C2BA] dark:text-white/50 sm:ml-4 sm:text-4xl md:text-5xl">
              {user ? `${user.name.split(" ")[0]}.` : ""}
            </span>
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-x-12 gap-y-6">
          <InfoGroup icon={Building2} label="Company" value={company.name} />
          <InfoGroup icon={Hash} label="Employee Number" value={contract.envelopeNumber} />
          <InfoGroup icon={Loader} label="Status" value={contract.status} />
        </div>
      </section>

      {/* Progress bar */}
      <section className="mt-10 flex flex-wrap items-center gap-3">
        <div className="mb-4 flex w-full flex-wrap items-center gap-3">
          <div className="flex flex-col">
            <span className="mb-1 text-[11px] font-medium text-[#8B8680] dark:text-white/45">
              Range
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="range-filter-trigger"
                  className="flex items-center gap-2 rounded-2xl bg-[#F0EBE5] px-4 py-3.5 text-sm font-semibold text-[#1A1815] outline-none transition-colors hover:bg-[#E7E0D8] dark:border dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                >
                  <CalendarDays className="h-4 w-4" strokeWidth={1.8} />
                  {RANGES.find((r) => r.key === range)?.label}
                  <ChevronDown className="h-4 w-4" strokeWidth={2.2} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                style={{ fontFamily: "Urbanist, sans-serif" }}
                className="w-48 rounded-2xl border-[#E7E0D8] bg-white p-1.5 shadow-lg dark:border-white/10 dark:bg-[#1A1721]"
              >
                {RANGES.map((r) => (
                  <DropdownMenuItem
                    key={r.key}
                    data-testid={`range-option-${r.key}`}
                    onClick={() => setRange(r.key)}
                    className="flex cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium text-[#1A1815] focus:bg-[#F0EBE5] dark:text-white dark:focus:bg-white/10"
                  >
                    {r.label}
                    {range === r.key && <Check className="h-4 w-4" strokeWidth={2.4} />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="flex flex-col">
            <span className="mb-1 text-[11px] font-medium text-[#8B8680] dark:text-white/45">
              {progress.signed.label}
            </span>
            <div className="glow-green flex items-center gap-3 rounded-2xl bg-[#7AFFA1] px-4 py-3.5 pr-16 text-black">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/15">
                <Check className="h-4 w-4" strokeWidth={2.4} />
              </span>
              <span className="text-lg font-bold tracking-tight">
                {progress.signed.amount}
              </span>
            </div>
          </div>

          <div className="flex flex-col">
            <span className="mb-1 text-[11px] font-medium text-[#8B8680] dark:text-white/45">
              {progress.pending.label}
            </span>
            <div className="glow-yellow flex items-center gap-3 rounded-2xl bg-[#FFF87C] px-4 py-3.5 pr-12 text-black">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/15">
                <Hourglass className="h-4 w-4" strokeWidth={2} />
              </span>
              <span className="text-lg font-bold tracking-tight">
                {progress.pending.amount}
              </span>
            </div>
          </div>

          <div className="flex flex-1 flex-col">
            <span className="mb-1 text-[11px] font-medium text-[#8B8680] dark:text-white/45">
              {progress.balance.label}
            </span>
            <div className="tick-pattern flex min-h-[54px] flex-1 items-center gap-3 rounded-2xl border border-[#E7E0D8] bg-[#FDFBFA] pl-4 pr-1.5 dark:border-white/10 dark:bg-white/[0.03]">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#F0EBE5] dark:border dark:border-white/10 dark:bg-white/5">
                <Hourglass className="h-3.5 w-3.5 text-[#1A1815] dark:text-white/60" strokeWidth={1.8} />
              </span>
              <span className="text-lg font-bold tracking-tight text-[#1A1815] dark:text-white/80">
                {progress.balance.amount}
              </span>
              <div className="ml-auto flex items-center gap-3">
                <span className="hidden text-sm font-semibold text-[#1A1815] dark:text-white/80 sm:block">
                  New Envelope
                </span>
                <ArrowRight
                  className="hidden h-4 w-4 text-[#1A1815] dark:text-white/80 sm:block"
                  strokeWidth={1.8}
                />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 rounded-xl bg-[#1A1815] px-7 py-3 text-sm font-semibold text-white outline-none transition-transform duration-200 hover:scale-[1.03] active:scale-95 dark:bg-white dark:text-black">
                      Start
                      <ChevronDown className="h-4 w-4" strokeWidth={2.4} />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    style={{ fontFamily: "Urbanist, sans-serif" }}
                    className="w-56 rounded-2xl border-[#E7E0D8] bg-white p-1.5 shadow-lg dark:border-white/10 dark:bg-[#1A1721]"
                  >
                    <DropdownMenuItem
                      onClick={() => navigate("/send")}
                      className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-[#1A1815] focus:bg-[#F0EBE5] dark:text-white dark:focus:bg-white/10"
                    >
                      <Send className="h-4 w-4" strokeWidth={1.8} />
                      Send an Envelope
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled
                      data-testid="start-sign-document"
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-[#B0A99F] opacity-60 data-[disabled]:pointer-events-none dark:text-white/30"
                    >
                      <FileSignature className="h-4 w-4" strokeWidth={1.8} />
                      Sign a Document
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TopSection;
